import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Wallet,
  Home,
  TrendingUp,
  Banknote,
  Building2,
  RefreshCw,
  Loader2,
  PiggyBank,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { DashboardLayout } from '../components/DashboardLayout';
import { useEntityStore } from '../store';
import { wealthAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';

const assetTypeConfig = {
  real_estate: { icon: Home, color: '#10B981', label: 'Real Estate' },
  trading: { icon: TrendingUp, color: '#3B82F6', label: 'Trading' },
  business_equity: { icon: Building2, color: '#8B5CF6', label: 'Business Equity' },
  cash: { icon: Banknote, color: '#F59E0B', label: 'Cash' },
};

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$', DKK: 'kr' };

const formatCurrency = (value, currency = 'GBP') => {
  return `${CURRENCY_SYMBOLS[currency] || currency}${value.toLocaleString('en-GB', { minimumFractionDigits: 0 })}`;
};

function AssetCard({ asset }) {
  const config = assetTypeConfig[asset.type] || { icon: Wallet, color: '#6B7280', label: asset.type };
  const Icon = config.icon;
  const gain = asset.current_value - asset.cost_basis;
  const gainPercent = asset.cost_basis > 0 ? ((gain / asset.cost_basis) * 100).toFixed(1) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card className="hover:shadow-lg transition-all h-full">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div
              className="p-3 rounded-xl"
              style={{ backgroundColor: `${config.color}20` }}
            >
              <Icon className="h-6 w-6" style={{ color: config.color }} />
            </div>
            <Badge variant="outline">{config.label}</Badge>
          </div>

          <h3 className="font-semibold text-lg mb-1 line-clamp-1">{asset.name}</h3>
          <p className="text-3xl font-bold font-mono mt-2">
            {formatCurrency(asset.current_value, asset.currency)}
          </p>

          <div className="mt-4 pt-4 border-t space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Cost Basis</span>
              <span className="font-mono">{formatCurrency(asset.cost_basis, asset.currency)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Gain/Loss</span>
              <span className={cn('font-mono font-medium', gain >= 0 ? 'text-green-600' : 'text-red-600')}>
                {gain >= 0 ? '+' : ''}{formatCurrency(gain, asset.currency)} ({gainPercent}%)
              </span>
            </div>
            {asset.income_generated > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Annual Income</span>
                <span className="font-mono text-green-600">
                  +{formatCurrency(asset.income_generated, asset.currency)}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function WealthPage() {
  const { selectedEntity, entities } = useEntityStore();
  const selectedEntityObj = selectedEntity !== 'all'
    ? entities.find((e) => e.id === selectedEntity)
    : null;
  const entityCurrency = selectedEntityObj?.currency || 'GBP';
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    try {
      // Pass entity UUID — resolve_entity_filter handles both UUID and type code formats
      const entityId = selectedEntity !== 'all' ? selectedEntity : undefined;

      const [assetsRes, summaryRes] = await Promise.all([
        wealthAPI.getAssets(entityId),
        wealthAPI.getSummary(entityId),
      ]);
      setAssets(assetsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (entities.length > 0 || selectedEntity === 'all') {
      setIsLoading(true);
      fetchData();
    }
  }, [selectedEntity, entities]);

  const pieData = summary?.by_type
    ? Object.entries(summary.by_type).map(([name, data]) => ({
        name: assetTypeConfig[name]?.label || name,
        value: data.value,
        color: assetTypeConfig[name]?.color || '#6B7280',
      }))
    : [];

  const totalValue = summary?.total_net_worth || 0;
  const totalIncome = summary?.total_annual_income || 0;
  // /wealth/summary converts every asset to GBP and always reports
  // currency: "GBP" on the response, regardless of which entity was
  // requested. entityCurrency describes the selected entity, not this
  // total, so label the total with the response's own currency instead.
  const totalsCurrency = summary?.currency || 'GBP';

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-8" data-testid="wealth-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Wealth Portfolio</h1>
            <p className="text-muted-foreground mt-1">Personal assets and net worth tracking</p>
          </div>
        </div>

        <div className="pt-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {/* Hero Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 bg-gradient-to-br from-emerald-900 to-emerald-950 text-white border-0">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-xl bg-white/10">
                      <Wallet className="h-6 w-6" />
                    </div>
                    <span className="text-emerald-200 font-medium">Total Net Worth</span>
                  </div>
                  <p className="text-5xl font-bold font-mono tracking-tight">
                    {formatCurrency(totalValue, totalsCurrency)}
                  </p>
                  <div className="mt-6 grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-emerald-200 text-sm">Annual Income</p>
                      <p className="text-2xl font-bold font-mono mt-1">
                        {formatCurrency(totalIncome, totalsCurrency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-emerald-200 text-sm">Asset Count</p>
                      <p className="text-2xl font-bold font-mono mt-1">{assets.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Allocation Pie */}
              <Card>
                <CardHeader>
                  <CardTitle>Asset Allocation</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                        }}
                        formatter={(value) => [formatCurrency(value, entityCurrency), 'Value']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2 mt-4">
                    {pieData.map((item) => (
                      <div key={item.name} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                          <span>{item.name}</span>
                        </div>
                        <span className="font-mono">{((item.value / totalValue) * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Asset Cards */}
            {assets.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Wallet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Assets</h3>
                  <p className="text-muted-foreground mb-4">
                    No wealth assets tracked yet. Add assets manually to start tracking your portfolio.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Real Estate */}
                {assets.filter((a) => a.type === 'real_estate').length > 0 && (
                  <div>
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Home className="h-5 w-5 text-emerald-500" />
                      Real Estate
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {assets.filter((a) => a.type === 'real_estate').map((asset) => (
                        <AssetCard key={asset.id} asset={asset} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Other Assets */}
                {assets.filter((a) => a.type !== 'real_estate').length > 0 && (
                  <div>
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <PiggyBank className="h-5 w-5 text-blue-500" />
                      Other Investments
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {assets.filter((a) => a.type !== 'real_estate').map((asset) => (
                        <AssetCard key={asset.id} asset={asset} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        </div>
      </div>
    </DashboardLayout>
  );
}
